import { CheckCircle2, Clock, Loader2, Circle } from 'lucide-react';
import { useGetInvestigationMilestonesQuery } from '@/store/api/apiSlice';
import type { MilestoneStatus } from '@/types';

interface Props {
    investigationId: string;
}

const STATUS_COPY: Record<MilestoneStatus, { label: string; tone: string }> = {
    submitted: { label: 'Submitted', tone: 'text-green-700' },
    in_progress: { label: 'In progress', tone: 'text-[#FF6B35]' },
    not_started: { label: 'Pending', tone: 'text-[#717680]' },
};

function StatusIcon({ status }: { status: MilestoneStatus }) {
    if (status === 'submitted') {
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    }
    if (status === 'in_progress') {
        return <Clock className="w-4 h-4 text-[#FF6B35]" />;
    }
    return <Circle className="w-4 h-4 text-[#bbb]" />;
}

function formatSubmittedAt(value: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

export default function MilestoneTimeline({ investigationId }: Props) {
    const { data, isLoading, error } = useGetInvestigationMilestonesQuery(investigationId);

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 text-[#FF6B35] animate-spin" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                Couldn't load milestone progress for this investigation.
            </div>
        );
    }

    const progressPct =
        data.totalMilestones > 0 ? Math.round((data.submittedMilestones / data.totalMilestones) * 100) : 0;

    return (
        <div className="flex flex-col gap-5">
            <div className="bg-white border border-[#e0e0e0] rounded-xl px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div>
                        <h3 className="text-sm font-bold text-[#1b1b1b]">Investigation progress</h3>
                        <p className="text-xs text-[#4e4d4d] mt-0.5">
                            Each milestone is a section of the investigator's checklist. Findings are revealed once the
                            full report is approved.
                        </p>
                    </div>
                    <span className="text-xs text-[#4e4d4d]">
                        <span className="font-semibold text-[#1b1b1b]">{data.submittedMilestones}</span> /{' '}
                        {data.totalMilestones} complete ({progressPct}%)
                    </span>
                </div>
                <div className="h-1.5 w-full bg-[#f0f0f0] rounded-full overflow-hidden">
                    <div
                        className="h-full bg-[#FF6B35] transition-all duration-300"
                        style={{ width: `${progressPct}%` }}
                    />
                </div>
            </div>

            <ol className="relative pl-6">
                <span className="absolute left-[10px] top-1 bottom-1 w-px bg-[#e0e0e0]" aria-hidden />
                {data.milestones.map((m) => {
                    const copy = STATUS_COPY[m.status];
                    return (
                        <li key={m.sectionId} className="relative pb-5 last:pb-0">
                            <span className="absolute -left-6 top-0.5 w-5 h-5 rounded-full bg-white border border-[#e0e0e0] flex items-center justify-center">
                                <StatusIcon status={m.status} />
                            </span>
                            <div className="flex flex-wrap items-baseline gap-2">
                                <p className="text-sm font-semibold text-[#1b1b1b]">
                                    {m.order}. {m.title}
                                </p>
                                <span className={`text-[11px] font-medium ${copy.tone}`}>{copy.label}</span>
                            </div>
                            {m.status === 'submitted' && m.submittedAt && (
                                <p className="text-xs text-[#4e4d4d] mt-0.5">
                                    ✓ Submitted on {formatSubmittedAt(m.submittedAt)}
                                </p>
                            )}
                            {m.status === 'in_progress' && (
                                <p className="text-xs text-[#4e4d4d] mt-0.5">Agent is working on this section.</p>
                            )}
                            {m.status === 'not_started' && (
                                <p className="text-xs text-[#717680] mt-0.5">Not started yet.</p>
                            )}
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
